import { useEffect, useRef, useState } from "react";
import { Instagram, RefreshCw, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { IG } from "@/constants/testIds";

// The Meta embed script processes any <blockquote class="instagram-media"> in the DOM.
// It's cached globally on the window so we don't re-inject on every refresh.
const IG_EMBED_SCRIPT = "https://www.instagram.com/embed.js";

const loadEmbedScript = () =>
  new Promise((resolve) => {
    if (window.instgrm?.Embeds) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${IG_EMBED_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const s = document.createElement("script");
    s.src = IG_EMBED_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    document.body.appendChild(s);
  });

const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/instagram/feed", { params: { limit: 12 } });
      setPosts(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Whenever the posts list changes, ask Meta's script to (re)render the embeds.
  useEffect(() => {
    if (posts.length === 0) return;
    let cancelled = false;
    loadEmbedScript().then(() => {
      if (cancelled) return;
      window.instgrm?.Embeds?.process();
    });
    return () => {
      cancelled = true;
    };
  }, [posts]);

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
        Redes Sociais
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-6">
        @falaitapira
      </h1>

      <div className="flex items-center justify-between mb-5 bg-white rounded-xl p-4 shadow-[0_6px_24px_rgba(13,27,75,0.06)]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full fi-gradient flex items-center justify-center">
            <div className="w-[42px] h-[42px] rounded-full bg-white flex items-center justify-center">
              <Instagram className="w-5 h-5 text-[var(--fi-blue)]" />
            </div>
          </div>
          <div>
            <div className="font-display font-extrabold text-[var(--fi-blue)] leading-tight">
              @falaitapira
            </div>
            <div className="text-xs text-gray-500">Publicações em destaque</div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            data-testid={IG.refresh}
            onClick={load}
            className="w-10 h-10 rounded-full border border-gray-200 hover:border-[var(--fi-blue)] flex items-center justify-center transition-colors"
            aria-label="Atualizar feed"
          >
            <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? "animate-spin" : ""}`} />
          </button>
          <a
            data-testid={IG.follow}
            href="https://instagram.com/falaitapira"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs uppercase tracking-widest font-bold fi-gradient hover:opacity-90 transition-opacity"
          >
            Seguir
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {!loading && posts.length === 0 && (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-200 bg-white">
          <Instagram className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <div className="text-gray-500 font-medium">Nenhuma publicação em destaque no momento.</div>
          <a
            href="https://instagram.com/falaitapira"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-white text-xs uppercase tracking-widest font-bold fi-gradient hover:opacity-90"
          >
            Ver no Instagram
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {/* Meta official embeds — each blockquote is rendered by embed.js */}
      <div
        ref={gridRef}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid={IG.grid || "ig-embed-grid"}
      >
        {posts.map((p) => (
          <blockquote
            key={p.id}
            data-testid={IG.post ? IG.post(p.id) : `ig-post-${p.id}`}
            className="instagram-media"
            data-instgrm-permalink={p.permalink}
            data-instgrm-version="14"
            style={{
              background: "#FFF",
              border: 0,
              borderRadius: "12px",
              boxShadow: "0 6px 24px rgba(13,27,75,0.06)",
              margin: 0,
              maxWidth: "540px",
              minWidth: "280px",
              width: "100%",
            }}
          >
            <a href={p.permalink} target="_blank" rel="noopener noreferrer">
              Ver publicação no Instagram
            </a>
          </blockquote>
        ))}
      </div>
    </section>
  );
};

export default InstagramFeed;
